import {
  parseOtaMetadata,
  validateVersionMatch,
  isEspMetadata,
  getVersionFromMetadata,
} from '@marsinet/commons';

export const parseFileMetadata = async (
  file: File,
  setFileMetadata,
  setMetadataError,
  setVersionMismatchWarning,
  version,
  toastRef,
  t
) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const fileBuffer = new Uint8Array(arrayBuffer);

    const result = parseOtaMetadata(fileBuffer as any);

    if (result.valid && result.metadata) {
      setFileMetadata(result.metadata);
      setMetadataError(null);

      // Detectar tipo de firmware
      const isEsp = isEspMetadata(result.metadata);

      // Si hay versión ya introducida, re-validar contra los nuevos metadatos
      if (version.trim() && isValidVersion(version)) {
        const metadataVersion = getVersionFromMetadata(result.metadata);
        const validationResult = validateVersionMatch(version, metadataVersion);
        if (!validationResult.match) {
          setVersionMismatchWarning(
            formatVersionMismatchWarning(
              validationResult.message || 'Version mismatch with file metadata'
            )
          );
        } else {
          setVersionMismatchWarning(null);
        }
      }
    } else {
      setFileMetadata(null);
      const friendlyError = formatMetadataError(result.error);
      setMetadataError(friendlyError || 'Unknown error parsing metadata');
      toastRef.current?.show({
        severity: 'error',
        summary: t('messages.error'),
        detail: friendlyError || result.error,
        life: 4000,
      });
    }
  } catch (error) {
    console.error('Error parsing file metadata:', error);
    setFileMetadata(null);
    setMetadataError(t('pages.maintenance.ota.error_reading_file_metadata'));
  }
};

export const formatVersionMismatchWarning = (message: string): string => {
  if (!message) return '';

  // Traducir mensaje de version mismatch
  // Ejemplo: "Version mismatch: input 4.1.0 vs metadata 4.1.1"
  const match = message.match(/input\s+([\d.]+)\s+vs\s+metadata\s+([\d.]+)/);
  if (match) {
    const inputVersion = match[1];
    const metadataVersion = match[2];
    return `No coincide la versión. Versión ingresada ${inputVersion} vs versión en archivo ${metadataVersion}`;
  }

  return message;
};

export const formatMetadataError = (error: string | undefined): string | null => {
  if (!error) return null;

  // Mapear errores técnicos a mensajes más amigables
  if (error.includes('Invalid magic numbers')) {
    return 'El archivo no es un firmware válido. Asegúrate de seleccionar un archivo de firmware correcto para el tipo de software elegido.';
  }
  if (error.includes('Cannot find module')) {
    return 'Formato de firmware no reconocido.';
  }
  if (error.includes('version')) {
    return 'No se pudo leer la versión del firmware del archivo.';
  }
  if (error.includes('size') || error.includes('length')) {
    return 'El archivo de firmware parece estar corrupto o incompleto.';
  }

  return error;
};

export const calculateFileChecksum = async (
  file: File,
  setFileChecksum: React.Dispatch<React.SetStateAction<string | null>>
) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    setFileChecksum(hashHex);
  } catch (error) {
    console.error('Error calculating file checksum:', error);
    setFileChecksum(null);
  }
};

export const isValidVersion = (ver: string): boolean => {
  const semverRegex = /^\d+\.\d+\.\d+$/;
  return semverRegex.test(ver.trim());
};

export const isValidUploadToken = (token: string): boolean => {
  // Token formato: ota_<32 caracteres hex>
  return /^ota_[a-f0-9]{48}$/.test(token.trim());
};
